// src/templates/CustomerShell.tsx
//
// Instagram-style customer chrome.
//
//   Mobile: minimal top header (brand-mark only) + sticky bottom nav
//           with 5 tabs — Home / Plan / DMs / Search / Me. The DMs
//           tab sits in the centre slot, like Instagram's reels button.
//   Desktop: same 5 items as a left sidebar (240 px wide), with the
//           main content reserved on the right. Top header collapses.
//
// Hidden on certain "deep" routes (thread detail, vendor profile)
// where the keyboard / sticky CTA needs the bottom space.

import { type ReactNode } from 'react'
import { Link, NavLink, useLocation, matchPath } from 'react-router-dom'
import {
  Home,
  Calendar,
  MessageCircle,
  Search,
  User,
} from 'lucide-react'

interface Props {
  children: ReactNode
}

interface Tab {
  to: string
  label: string
  icon: typeof Home
  /** end=true means "exact match only" (used for the Home tab so it
   *  isn't permanently active because the URL starts with /). */
  end?: boolean
}

const TABS: Tab[] = [
  { to: '/discover', label: 'Home', icon: Home, end: true },
  { to: '/account/plan', label: 'Plan', icon: Calendar },
  { to: '/account/inbox', label: 'DMs', icon: MessageCircle },
  { to: '/discover?focus=search', label: 'Search', icon: Search },
  { to: '/account/me', label: 'Me', icon: User },
]

// Routes where the bottom nav should be hidden so the page has full
// height for keyboard / sticky CTA.
const HIDE_NAV_PATTERNS = [
  '/account/inbox/:id',
  '/v/:slug',
]

function shouldHideNav(pathname: string): boolean {
  return HIDE_NAV_PATTERNS.some(pattern => matchPath(pattern, pathname) !== null)
}

export function CustomerShell({ children }: Props) {
  const location = useLocation()
  const navHidden = shouldHideNav(location.pathname)

  return (
    <div className="min-h-screen bg-bone flex flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-60 md:shrink-0 md:border-r md:border-ink/05 md:bg-cream md:sticky md:top-0 md:h-screen md:py-6">
        <Link to="/discover" className="flex items-center gap-2 px-5 mb-8" aria-label="PlanSync home">
          <div className="brand-mark brand-mark--sm">p</div>
          <span className="font-serif text-lg font-semibold tracking-tight">
            PlanSync
          </span>
        </Link>
        <nav className="flex flex-col gap-1 px-3">
          {TABS.map(tab => (
            <SidebarLink key={tab.to} tab={tab} />
          ))}
        </nav>
      </aside>

      {/* Right side: content + mobile bottom nav.
          No top header here — individual pages bring their own
          (back-arrow, page title, filters, etc.) so we don't double up. */}
      <div className="flex-1 min-w-0 flex flex-col">
        <main
          className={`flex-1 min-w-0 ${navHidden ? '' : 'pb-20 md:pb-0'}`}
        >
          {children}
        </main>

        {/* Mobile bottom nav */}
        {!navHidden && (
          <nav
            className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-cream/95 backdrop-blur-md border-t border-ink/05 grid grid-cols-5 pb-[env(safe-area-inset-bottom)]"
            aria-label="Customer navigation"
          >
            {TABS.map(tab => (
              <BottomNavItem key={tab.to} tab={tab} />
            ))}
          </nav>
        )}
      </div>
    </div>
  )
}

function SidebarLink({ tab }: { tab: Tab }) {
  // For the Search tab we want the link to MATCH /discover only when
  // the search query is in the URL. NavLink's `end` prop handles plain
  // path matching, but we use a hand-rolled active check so /discover
  // (Home) and /discover?focus=search (Search) light up correctly.
  const isSearch = tab.label === 'Search'
  const Icon = tab.icon
  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      className={({ isActive }) => {
        const active = activeForTab(tab, isActive)
        return `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          active ? 'bg-ink text-white' : 'text-ink-70 hover:bg-orange-mist hover:text-ink'
        }`
      }}
    >
      <Icon className="w-5 h-5" />
      {tab.label}
      {isSearch && <span className="sr-only">opens vendor search</span>}
    </NavLink>
  )
}

function BottomNavItem({ tab }: { tab: Tab }) {
  const Icon = tab.icon
  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      className={({ isActive }) => {
        const active = activeForTab(tab, isActive)
        return `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold tracking-[0.02em] ${
          active ? 'text-orange-deep' : 'text-ink-50'
        }`
      }}
    >
      <Icon className="w-5 h-5" />
      {tab.label}
    </NavLink>
  )
}

/**
 * Refine NavLink's isActive for our two-tab-one-path edge case:
 *  - "Home" should be active on /discover when there's no ?focus=search
 *  - "Search" should be active on /discover?focus=search
 * Everything else: trust NavLink's default isActive.
 */
function activeForTab(tab: Tab, defaultActive: boolean): boolean {
  if (typeof window === 'undefined') return defaultActive
  const params = new URLSearchParams(window.location.search)
  const searchFocused = params.get('focus') === 'search'
  if (tab.label === 'Home') {
    return defaultActive && !searchFocused
  }
  if (tab.label === 'Search') {
    return window.location.pathname === '/discover' && searchFocused
  }
  return defaultActive
}
